import React from 'react'

import styles from './_popover-header.module.scss'
import { Button, Icon, type IconStringList } from '../../module'

type PopoverHeaderBase = {
  /** Determines the color of the background and icon. */
  styleType?: 'lighter-gray' | 'green' | 'red' | 'blue' | 'black'
  /** Optional function to call when using the close button. */
  closeCallout?: () => void
  /** Optional icon to include at the left of the header. */
  icon?: IconStringList
  /** Optionally remove the border radius at the top. Should be used for instances like `SideDrawer` */
  noBorderRadius?: boolean
  /** Optionally add a back button to the header. This is useful for cases like having multiple `SideDrawer` layers. */
  hasBackButton?: boolean
  /** Optional prop to add a test id to the PopoverHeader for QA testing */
  qaTestId?: string
}

type PopoverHeaderWithText = PopoverHeaderBase & {
  /** Text for the header. This is required if a `logoUrl` is not present. */
  headerText: string
  logoUrl?: never
}

type PopoverHeaderWithLogo = PopoverHeaderBase & {
  headerText?: never
  /** Logo for the header. This is required if a `headerText` is not present. */
  logoUrl: string
}

export type PopoverHeaderProps = PopoverHeaderWithText | PopoverHeaderWithLogo

const PopoverHeader = ({
  headerText,
  icon,
  styleType = 'lighter-gray',
  closeCallout,
  noBorderRadius,
  logoUrl,
  hasBackButton,
  qaTestId = 'popover-header',
}: PopoverHeaderProps): React.JSX.Element => {
  const iconColor =
    styleType !== 'lighter-gray' && styleType !== 'black'
      ? styles.whiteIcon
      : styles.purpleIcon

  return (
    <div
      className={`${styles.container} ${styles[styleType]} ${
        noBorderRadius ? styles.noBorderRadius : ''
      }`}
      qa-test-id={qaTestId}
    >
      <div className={styles.headerTextContainer}>
        {hasBackButton ? (
          <Button
            as='unstyled'
            onClick={closeCallout}
            className={styles.backButton}
          >
            <Icon
              icon='arrowLong'
              className={`back-button-icon ${styles.backButtonIcon}`}
            />
          </Button>
        ) : null}
        {icon ? (
          <Icon
            icon={icon}
            iconSize='16px'
            className={`popover-header-icon ${iconColor}`}
          />
        ) : null}
        {logoUrl ? (
          <img src={logoUrl} alt='logo' className={styles.logo} />
        ) : (
          <span>{headerText}</span>
        )}
      </div>
      {closeCallout ? (
        <Button as='unstyled' onClick={closeCallout}>
          <Icon
            icon='x'
            iconSize='16px'
            // Keep the popover-header-icon to help style these icons. This is a workaround because the Icon styles gets built after these styles so they are not applied correctly. Component is encapsulated correctly.
            className={`popover-header-icon ${iconColor}`}
          />
        </Button>
      ) : null}
    </div>
  )
}

export default PopoverHeader
